import * as z from 'zod';
import {
  addressSchema,
  dayScheduleSchema,
  merchantModerationStateSchema,
  optional,
  restaurantStatusSchema,
  timestampSchema,
  workingHoursSchema,
} from './common';
import { localizedTextSchema, pendingChangeRequestSchema } from './product';

// Restaurant Service — `v1Restaurant` (a branch of the merchant), its delivery
// pricing, its weekly schedule, and the single-resource envelopes.

export const restaurantSchema = z.object({
  id: z.string(),
  name: z.string().default(''),
  address: addressSchema.optional(),
  status: restaurantStatusSchema.optional(),
  working_hours: workingHoursSchema.optional(),
  /** Missing on older backends means accepting; keep false distinct from absence. */
  accepting_orders_now: z.boolean().optional(),
  rating: z.number().default(0),
  icon: z.string().default(''),
  total_order_count: z.number().int().nonnegative().default(0),
  min_prep_time: z.number().int().nonnegative().default(0),
  max_prep_time: z.number().int().nonnegative().default(0),
  created_at: optional(timestampSchema),
  updated_at: optional(timestampSchema),
  likes_count: z.number().int().nonnegative().default(0),
  is_liked: z.boolean().default(false),
  menu_source: z.string().default(''),
  /**
   * Discovery-chip tag ids (home-screen taxonomy). Names resolve through the
   * Restaurant Category Service — see `model/restaurant-category`.
   */
  restaurant_category_ids: z.array(z.string()).default([]),
  pickup_available: z.boolean().optional(),
  is_popular: z.boolean().optional(),
  cuisines: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
  external_id: z.string().default(''),
  /** Minimum basket total for free delivery; `0` = no free-delivery offer. */
  free_delivery_threshold: z.number().int().nonnegative().default(0),
  /** Minimum order total in so'm; absent/null = the branch has no minimum. */
  min_order_amount: z.number().int().nonnegative().nullish(),
  /** Branch moderation state; `UNSPECIFIED` means no badge/filter verdict. */
  moderation_state: merchantModerationStateSchema
    .optional()
    .default('MERCHANT_MODERATION_STATE_UNSPECIFIED'),
  /** The moderator's note when `moderation_state` is REJECTED. */
  rejection_reason: z.string().optional(),
  /** Owning merchant (company). Absent on restaurants not yet assigned. */
  merchant_id: z.string().optional(),
  description: z.string().default(''),
  phone: z.string().default(''),
  background_image: z.string().default(''),
  delivery_provider: z.string().default(''),
});
export type Restaurant = z.infer<typeof restaurantSchema>;

/** Single-resource payload — `data` on get/update/open/close. */
export const restaurantResponseSchema = z.object({
  restaurant: restaurantSchema,
});

/**
 * `v1LocalizedRestaurant` — the branch with EVERY translation at once, which is
 * what `GET /restaurants/{id}/by-merchant` answers with (the plain
 * `GET /restaurants/{id}` resolves to the request's `x-locale` instead).
 *
 * This is what seeds the branch editor: the form captures ru/uz/en per field,
 * and a locale-resolved read carries only one of the three. Same shape and
 * purpose as `localizedProductSchema`.
 */
export const localizedRestaurantSchema = restaurantSchema.extend({
  name: localizedTextSchema.default({ ru: '', uz: '', en: '' }),
  description: localizedTextSchema.default({ ru: '', uz: '', en: '' }),
  address: z
    .object({
      line: localizedTextSchema.default({ ru: '', uz: '', en: '' }),
      latitude: z.number().default(0),
      longitude: z.number().default(0),
    })
    .optional(),
});
export type LocalizedRestaurant = z.infer<typeof localizedRestaurantSchema>;

/**
 * `GET /restaurants/{id}/by-merchant`'s full envelope: the localized branch
 * under `data.restaurant` plus its 7-day schedule as a sibling top-level
 * `list`, so the editor seeds every field in one call.
 */
export const localizedRestaurantWithScheduleResponseSchema = z.object({
  data: z.object({ restaurant: localizedRestaurantSchema }),
  list: z.array(dayScheduleSchema).default([]),
});

/**
 * Single-resource payload for published restaurant read (`GET /restaurants/{id}/published`).
 * Returned as `{ client_visible: true, restaurant }` or `{ client_visible: false, restaurant: null }`.
 */
export const publishedRestaurantCardResponseSchema = z.object({
  client_visible: z.boolean().default(true),
  restaurant: localizedRestaurantSchema
    .optional()
    .nullable()
    .or(restaurantSchema.optional().nullable()),
});
export type PublishedRestaurantCardResponse = z.infer<
  typeof publishedRestaurantCardResponseSchema
>;

/**
 * `GET /restaurants/{id}`'s full envelope: the branch under `data.restaurant`
 * plus its weekly schedule as a sibling top-level `list` — the same `list` the
 * now-deprecated `GET /restaurants/{id}/working-hours` returned on its own.
 * `list` is absent on `update`/`open`/`close`, so it defaults to `[]` rather
 * than being required.
 */
export const restaurantWithScheduleResponseSchema = z.object({
  data: restaurantResponseSchema,
  list: z.array(dayScheduleSchema).default([]),
});

/**
 * `PATCH /restaurants/{id}`'s full envelope (`v1UpdateRestaurantResponse`).
 *
 * `data.restaurant` is a `v1LocalizedRestaurant` when the write applied, and
 * ABSENT when it was gated behind moderation (the wire then carries
 * `{result:'pending_change', pending_change:{…}}` — verified live). The sibling
 * `list` is the saved 7-day schedule, only meaningful on the applied branch.
 *
 * The restaurant payload is deliberately parsed only for its `id`: callers
 * re-read the branch through the read RPCs (whose locale-resolved shape is
 * what the view models consume), so modelling the whole localized profile here
 * would duplicate a shape nothing renders.
 */
export const restaurantWriteResponseSchema = z.object({
  data: z.object({
    restaurant: z.object({ id: z.string().default('') }).optional(),
    pending_change: pendingChangeRequestSchema.optional(),
  }),
  list: z.array(dayScheduleSchema).default([]),
});
export type RestaurantWriteResult = z.infer<typeof restaurantWriteResponseSchema>;

/** `v1DeliveryPricing` — the branch's delivery fee formula. */
export const deliveryPricingSchema = z.object({
  restaurant_id: z.string().default(''),
  /** e.g. `noor_managed`; free-form on the wire. */
  mode: z.string().default(''),
  free_distance_m: z.number().int().nonnegative().default(0),
  base_fee: z.number().int().nonnegative().default(0),
  price_per_km: z.number().int().nonnegative().default(0),
});
export type DeliveryPricing = z.infer<typeof deliveryPricingSchema>;

/** Single-resource payload — `data` on GET delivery pricing. */
export const deliveryPricingResponseSchema = z.object({
  delivery_pricing: deliveryPricingSchema,
});

/**
 * `data` on PUT delivery pricing (`v1SetRestaurantDeliveryPricingResponse`).
 * Note the key is `pricing` here — NOT the GET's `delivery_pricing` — and it
 * is absent when the write was gated behind moderation.
 */
export const deliveryPricingWriteResponseSchema = z.object({
  pricing: deliveryPricingSchema.optional(),
  pending_change: pendingChangeRequestSchema.optional(),
});
export type DeliveryPricingWriteResult = z.infer<
  typeof deliveryPricingWriteResponseSchema
>;

/** The weekly schedule: seven `v1DaySchedule` rows, returned as a list. */
export const weeklyScheduleSchema = z.array(dayScheduleSchema);
export type WeeklySchedule = z.infer<typeof weeklyScheduleSchema>;
